import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiMapPin } from "react-icons/fi";
import LoadingOverlay from "../components/LoadingOverlay";
import { apiURL } from "../config/config";
import TravelExpenseForm from "../components/travelExpense/TravelExpenseForm";
import {
  getFileType,
  getReceiptUrls,
} from "../components/travelExpense/TravelExpenseApprovalUtils";
import {
  canCreateTravelExpense,
  isAdmin,
  isTravelTollOnlyUser,
} from "../utils/buttonVisibility";
import {
  MAX_RECEIPTS,
  buildEstimateFromRecord,
  buildQuotationDisplayNumber,
  buildTravelExpenseDepartureDateTime,
  canEditTravelExpenseRecord,
  createEmptyTravelExpenseForm,
  getQuotationPayloadNumber,
  mapRecordToTravelExpenseFormData,
  normalizeTravelExpenseAssetUrl,
  normalizeTravelExpenseRecords,
  optimizeReceiptFiles,
} from "./travelExpenseRequest.utils";

const TravelExpenseRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const canSubmitRequest = canCreateTravelExpense();
  const isTollOnlyRequester = isTravelTollOnlyUser();
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimateResult, setEstimateResult] = useState(null);
  const [quotationOptions, setQuotationOptions] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [existingReceiptPreviews, setExistingReceiptPreviews] = useState([]);
  const [signedReceiptCache, setSignedReceiptCache] = useState({});
  const [formData, setFormData] = useState(createEmptyTravelExpenseForm);

  useEffect(() => {
    if (canSubmitRequest) return;

    toast.error("Level 5 or higher users cannot create travel expense requests");
    navigate("/travel-expense", { replace: true });
  }, [canSubmitRequest, navigate]);

  const receiptPreviewUrls = useMemo(
    () =>
      formData.tollReceipts.map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        url: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
      })),
    [formData.tollReceipts]
  );

  useEffect(
    () => () => {
      receiptPreviewUrls.forEach((item) => {
        if (item.url) {
          URL.revokeObjectURL(item.url);
        }
      });
    },
    [receiptPreviewUrls]
  );

  const quotationYear = useMemo(
    () =>
      formData.departureDate instanceof Date
        ? formData.departureDate.getFullYear()
        : new Date().getFullYear(),
    [formData.departureDate]
  );

  const getSignedReceiptUrl = useCallback(
    async (receiptUrl) => {
      const cachedReceipt = signedReceiptCache[receiptUrl];
      const now = Date.now();

      if (cachedReceipt?.url && cachedReceipt.expiresAt > now) {
        return cachedReceipt.url;
      }

      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${apiURL}upload/receipt/signed-url`,
        { url: receiptUrl },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const signedReceipt = response.data?.data;
      const signedUrl = signedReceipt?.url;
      const expiresIn = Number(signedReceipt?.expiresIn || 0);

      if (!signedUrl) {
        throw new Error("Signed receipt URL not found");
      }

      setSignedReceiptCache((prev) => ({
        ...prev,
        [receiptUrl]: {
          url: signedUrl,
          expiresAt: now + Math.max(expiresIn - 30, 0) * 1000,
        },
      }));

      return signedUrl;
    },
    [signedReceiptCache]
  );

  useEffect(() => {
    let ignore = false;

    const fetchQuotations = async () => {
      try {
        setLoadingQuotations(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${apiURL}quotations?year=${quotationYear}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (ignore) return;

        const options = (response.data || []).map((quotation) => {
          const quotationDisplayNumber = buildQuotationDisplayNumber(quotation);
          const quotationNumber = getQuotationPayloadNumber(quotation);
          const quotationTitle =
            quotation.title || quotation.projectName || quotation.projectTitle || "";
          const projectName =
            quotation.projectName || quotation.title || quotation.projectTitle || "";

          return {
            value: quotation._id,
            label: `${quotationDisplayNumber} • ${projectName || quotationTitle || "ไม่มีชื่อโครงการ"}`,
            quotationId: quotation._id,
            quotationNumber,
            quotationYear: new Date(
              quotation.documentDate || quotation.createdAt || Date.now()
            ).getFullYear(),
            quotationType: quotation.type || "M",
            quotationTitle,
            projectName,
          };
        });

        setQuotationOptions(options);
      } catch (error) {
        console.error("Fetch quotations for travel expense error:", error);
        if (!ignore) {
          setQuotationOptions([]);
          toast.error(
            error?.response?.data?.message || "ไม่สามารถโหลดรายการ QT ได้"
          );
        }
      } finally {
        if (!ignore) {
          setLoadingQuotations(false);
        }
      }
    };

    fetchQuotations();

    return () => {
      ignore = true;
    };
  }, [quotationYear]);

  useEffect(() => {
    if (!isEditMode) {
      setExistingReceiptPreviews([]);
      return;
    }

    let ignore = false;

    const fetchRecord = async () => {
      try {
        setLoadingRecord(true);
        const token = localStorage.getItem("token");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const response = await axios.get(`${apiURL}travel-expenses`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (ignore) return;

        const records = normalizeTravelExpenseRecords(response.data);
        const canAccessAsAdmin = isAdmin();
        const record = records.find(
          (item) =>
            String(item?._id) === String(id) &&
            (canAccessAsAdmin ||
              String(item?.requestedBy || "").trim() ===
                String(user?.username || "").trim())
        );

        if (!record) {
          toast.error("ไม่พบรายการที่ต้องการแก้ไข");
          navigate("/travel-expense");
          return;
        }

        if (!canEditTravelExpenseRecord(record.status)) {
          toast.error("แก้ไขได้เฉพาะรายการที่มีสถานะ Pending หรือ Rejected เท่านั้น");
          navigate("/travel-expense");
          return;
        }

        setFormData(mapRecordToTravelExpenseFormData(record, getReceiptUrls));
        setEstimateResult(buildEstimateFromRecord(record, isTollOnlyRequester));
        const existingReceipts = await Promise.all(
          getReceiptUrls(record).map(async (url, index) => {
            const normalizedUrl = normalizeTravelExpenseAssetUrl(apiURL, url);
            const type = getFileType(normalizedUrl);
            let resolvedUrl = normalizedUrl;

            try {
              resolvedUrl = await getSignedReceiptUrl(url);
            } catch (receiptError) {
              console.error("Get signed receipt URL for edit preview error:", receiptError);
            }

            return {
              name: `ไฟล์เดิม ${index + 1}`,
              sourceUrl: url,
              url: resolvedUrl,
              previewUrl: type === "image" ? resolvedUrl : "",
              type,
            };
          })
        );

        if (ignore) return;

        setExistingReceiptPreviews(existingReceipts);
      } catch (error) {
        console.error("Fetch travel expense detail error:", error);
        toast.error(
          error?.response?.data?.message || "ไม่สามารถโหลดรายการที่ต้องการแก้ไขได้"
        );
        navigate("/travel-expense");
      } finally {
        if (!ignore) {
          setLoadingRecord(false);
        }
      }
    };

    fetchRecord();

    return () => {
      ignore = true;
    };
  }, [getSignedReceiptUrl, id, isEditMode, isTollOnlyRequester, navigate]);

  const selectedQuotationOption = useMemo(
    () =>
      quotationOptions.find((option) => option.value === formData.quotationId) ||
      null,
    [formData.quotationId, quotationOptions]
  );

  const handleQuotationChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      quotationId: option?.quotationId || "",
      quotationNumber: option?.quotationNumber || "",
      quotationYear:
        option?.quotationYear != null && option?.quotationYear !== ""
          ? String(option.quotationYear)
          : "",
      quotationType: option?.quotationType || "",
      quotationTitle: option?.quotationTitle || "",
      projectName: option?.projectName || "",
    }));
  };

  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "checkbox") {
      setEstimateResult(null);
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        ...(name === "useTollway" && !checked
          ? { tollFee: "", tollReceipts: [] }
          : {}),
      }));

      if (name === "useTollway" && !checked) {
        setExistingReceiptPreviews([]);
      }
      return;
    }

    if (type === "file") {
      const selectedFiles = Array.from(files || []).slice(0, MAX_RECEIPTS);

      if ((files || []).length > MAX_RECEIPTS) {
        toast.warning("อัปโหลดใบเสร็จทางด่วนได้สูงสุด 5 รูป");
      }

      const optimizedFiles = await optimizeReceiptFiles(selectedFiles);

      setFormData((prev) => ({
        ...prev,
        tollReceipts: optimizedFiles,
      }));
      e.target.value = "";
      return;
    }

    if (name === "origin" || name === "destination") {
      setEstimateResult(null);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRemoveSelectedReceipt = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tollReceipts: prev.tollReceipts.filter(
        (_, currentIndex) => currentIndex !== indexToRemove
      ),
    }));
  };

  const handleRemoveExistingReceipt = (indexToRemove) => {
    setExistingReceiptPreviews((prev) =>
      prev.filter((_, currentIndex) => currentIndex !== indexToRemove)
    );
  };

  const validateForm = () => {
    if (!formData.quotationId) {
      toast.error("กรุณาเลือก QT / โครงการที่ต้องการอ้างอิง");
      return false;
    }

    if (!formData.origin.trim()) {
      toast.error("กรุณากรอกต้นทาง");
      return false;
    }

    if (!formData.destination.trim()) {
      toast.error("กรุณากรอกปลายทาง");
      return false;
    }

    if (!formData.departureDate) {
      toast.error("กรุณาเลือกวันที่เดินทาง");
      return false;
    }

    if (!formData.departureTime) {
      toast.error("กรุณาเลือกเวลาออกเดินทาง");
      return false;
    }

    if (!formData.note.trim()) {
      toast.error("กรุณากรอกหมายเหตุ");
      return false;
    }

    if (formData.useTollway) {
      if (formData.tollFee === "" || Number(formData.tollFee) < 0) {
        toast.error("กรุณากรอกค่าทางด่วนให้ถูกต้อง");
        return false;
      }

      if (formData.tollReceipts.length > MAX_RECEIPTS) {
        toast.error("อัปโหลดใบเสร็จทางด่วนได้สูงสุด 5 รูป");
        return false;
      }

      if (
        formData.tollReceipts.length === 0 &&
        existingReceiptPreviews.length === 0
      ) {
        toast.error("กรุณาอัปโหลดรูปหรือไฟล์ใบเสร็จทางด่วน");
        return false;
      }
    }

    return true;
  };

  const handleEstimate = async () => {
    if (isTollOnlyRequester) return;

    if (!formData.origin.trim() || !formData.destination.trim()) {
      toast.error("กรุณากรอกต้นทางและปลายทางก่อนคำนวณระยะทาง");
      return;
    }

    try {
      setEstimating(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${apiURL}travel-expenses/estimate`,
        {
          origin: formData.origin,
          destination: formData.destination,
          useExpressway: formData.useTollway,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const estimatePayload = res.data?.data || {};
      const routeMeta = res.data?.routeMeta || {};

      setEstimateResult({
        origin: estimatePayload.origin || formData.origin,
        destination: estimatePayload.destination || formData.destination,
        distanceKm:
          estimatePayload.distanceKm ??
          routeMeta.distanceKm ??
          (routeMeta.distanceMeters ? Number(routeMeta.distanceMeters) / 1000 : 0),
        amount: estimatePayload.estimatedAmount ?? 0,
        estimatedAmount: estimatePayload.estimatedAmount ?? 0,
        duration: routeMeta.duration || "",
        ratePerKm: routeMeta.ratePerKm ?? null,
      });
      toast.success("คำนวณระยะทางเรียบร้อย");
    } catch (error) {
      console.error("Estimate travel expense error:", error);
      toast.error(
        error?.response?.data?.message || "ไม่สามารถคำนวณระยะทางได้"
      );
    } finally {
      setEstimating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitRequest) return;
    if (!validateForm()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const basePayload = {
        origin: formData.origin.trim(),
        destination: formData.destination.trim(),
        departureDateTime: buildTravelExpenseDepartureDateTime(
          formData.departureDate,
          formData.departureTime
        ),
        note: formData.note.trim(),
        tollFee: formData.useTollway ? Number(formData.tollFee || 0) : 0,
        quotationId: formData.quotationId,
        quotationNumber: formData.quotationNumber,
        quotationYear: Number(formData.quotationYear || 0),
        quotationType: formData.quotationType,
        quotationTitle: formData.quotationTitle,
        projectName: formData.projectName,
        avoidTolls: false,
        avoidHighways: false,
        useExpressway: Boolean(formData.useTollway),
        useTolls: Boolean(formData.useTollway),
      };

      if (isEditMode) {
        const payload = new FormData();
        Object.entries({
          ...basePayload,
          transportationType: "Car",
          requestedBy: user.username || "",
          amount: isTollOnlyRequester ? "0" : String(estimateResult?.amount || 0),
          estimatedAmount:
            isTollOnlyRequester ? "0" : String(estimateResult?.amount || 0),
        }).forEach(([key, value]) => {
          payload.append(key, value);
        });

        payload.append(
          "retainedTollReceiptUrls",
          JSON.stringify(
            existingReceiptPreviews
              .map((item) => item.sourceUrl || "")
              .filter(Boolean)
          )
        );

        formData.tollReceipts.forEach((file) => {
          payload.append("tollReceipts", file);
        });

        await axios.patch(`${apiURL}travel-expenses/${id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("อัปเดตรายการเบิกค่าเดินทางเรียบร้อย");
        navigate("/travel-expense");
        return;
      }

      if (formData.tollReceipts.length > 0) {
        const payload = new FormData();
        Object.entries(basePayload).forEach(([key, value]) => {
          payload.append(key, value);
        });

        formData.tollReceipts.forEach((file) => {
          payload.append("tollReceipts", file);
        });

        await axios.post(`${apiURL}travel-expenses`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await axios.post(`${apiURL}travel-expenses`, basePayload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      toast.success("บันทึกคำขอเบิกค่าเดินทางสำเร็จ");

      setFormData(createEmptyTravelExpenseForm());
      setEstimateResult(null);
      setExistingReceiptPreviews([]);
    } catch (error) {
      console.error(
        `${isEditMode ? "Update" : "Create"} travel expense error:`,
        error
      );
      toast.error(
        error?.response?.data?.message || "ไม่สามารถบันทึกข้อมูลได้"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!canSubmitRequest) return null;

  return (
    <div className="px-4 sm:px-6 py-5 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <LoadingOverlay loading={loading || loadingRecord} />

      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-2xl p-4 sm:p-6 transition-colors duration-300">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-700 dark:text-cyan-300">
              <FiMapPin size={20} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {isEditMode ? "Edit Travel Expense Request" : "Travel Expense Request"}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isEditMode
                  ? "แก้ไขข้อมูลคำขอเดิมและบันทึกกลับเข้าระบบ"
                  : "บันทึกค่าเดินทางและแนบข้อมูลทางด่วนก่อนส่งคำขอ"}
              </p>
            </div>
          </div>
        </div>

        <TravelExpenseForm
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          quotationOptions={quotationOptions}
          selectedQuotationOption={selectedQuotationOption}
          onQuotationChange={handleQuotationChange}
          loadingQuotations={loadingQuotations}
          handleSubmit={handleSubmit}
          handleEstimate={handleEstimate}
          loading={loading || loadingRecord}
          estimating={estimating}
          estimateResult={estimateResult}
          setEstimateResult={setEstimateResult}
          receiptPreviewUrls={receiptPreviewUrls}
          existingReceiptPreviews={existingReceiptPreviews}
          onRemoveExistingReceipt={handleRemoveExistingReceipt}
          onRemoveSelectedReceipt={handleRemoveSelectedReceipt}
          maxReceipts={MAX_RECEIPTS}
          isTollOnlyRequester={isTollOnlyRequester}
          submitLabel={isEditMode ? "Save Changes" : "Submit"}
        />
      </div>
    </div>
  );
};

export default TravelExpenseRequest;
